import {
  practiceDate,
  shiftPracticeDate,
  GuestProgressPacket,
  lessonTransferBounds,
} from "@chesshotel/types";
import { api } from "@/services/api";
import {
  getAllGuestLessonProgress,
  clearTransferredGuestLessons,
  GuestLessonProgress,
} from "../lessons/guestLessonProgress";
import {
  guestActivitySnapshot,
  clearTransferredGuestActivity,
} from "./guestLearningProgress";
import { refreshLearning } from "./learningEvents";

const KEY = "chesshotel:guest-transfer:v1";
type Pending = {
  userId: number;
  packet: GuestProgressPacket;
  activity: ReturnType<typeof guestActivitySnapshot>;
  lessons: Record<string, GuestLessonProgress>;
};
let memory: Pending | null = null;
let inFlight: Promise<boolean> | null = null;
function read(): Pending | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    const value = raw ? JSON.parse(raw) : null;
    if (
      value &&
      Number.isSafeInteger(value.userId) &&
      value.packet?.version === 1 &&
      Array.isArray(value.activity?.activities) &&
      value.lessons
    )
      return value;
    return null;
  } catch {
    /* Storage is optional. */
  }
  return memory;
}
function write(value: Pending | null) {
  memory = value;
  try {
    value
      ? window.localStorage.setItem(KEY, JSON.stringify(value))
      : window.localStorage.removeItem(KEY);
  } catch {
    /* Keep session fallback. */
  }
  refreshLearning();
}
export function hasGuestProgress() {
  return (
    guestActivitySnapshot().activities.length > 0 ||
    Object.values(getAllGuestLessonProgress()).some(
      (l) =>
        !!lessonTransferBounds(l.lessonId) &&
        (l.completedLines.length > 0 ||
          l.completedChallengeLines.length > 0 ||
          (Array.isArray(l.practiceLineProgress) &&
            l.practiceLineProgress.some((n) => n > 0)) ||
          (Array.isArray(l.challengeLineProgress) &&
            l.challengeLineProgress.some((n) => n > 0))),
    )
  );
}
export function pendingGuestTransfer(userId: number): boolean {
  return read()?.userId === userId;
}
export function canOfferGuestTransfer(userId: number): boolean {
  return !read() && hasGuestProgress() && !isDismissed(userId);
}
function isDismissed(userId: number) {
  try {
    return (
      window.localStorage.getItem(
        `chesshotel:guest-transfer-dismissed:${userId}`,
      ) === "true"
    );
  } catch {
    return false;
  }
}
export function dismissGuestTransfer(userId: number) {
  if (pendingGuestTransfer(userId)) write(null);
  try {
    window.localStorage.setItem(
      `chesshotel:guest-transfer-dismissed:${userId}`,
      "true",
    );
  } catch {}
  refreshLearning();
}
export function queueGuestTransfer(userId: number): boolean {
  if (read()) return pendingGuestTransfer(userId);
  if (!hasGuestProgress()) return false;
  const activity = guestActivitySnapshot();
  const lessons = Object.fromEntries(
    Object.entries(getAllGuestLessonProgress()).filter(
      ([id]) => !!lessonTransferBounds(id),
    ),
  );
  const today = practiceDate(new Date(), activity.timezone);
  const earliest = shiftPracticeDate(today, -3650);
  const transferId = Array.from(
    window.crypto.getRandomValues(new Uint8Array(16)),
    (n) => n.toString(16).padStart(2, "0"),
  ).join("");
  const packet: GuestProgressPacket = {
    version: 1,
    transferId,
    timezone: activity.timezone,
    dates: Array.from(
      new Set(
        activity.activities
          .map((a) => a.date)
          .filter((d) => d >= earliest && d <= today),
      ),
    ).sort(),
    lessons: Object.values(lessons).map((l) => ({
      ...l,
      practiceLineProgress: l.practiceLineProgress || [],
      challengeLineProgress: l.challengeLineProgress || [],
    })),
  };
  write({ userId, packet, activity, lessons });
  return true;
}
export async function retryGuestTransfer(userId: number): Promise<boolean> {
  const pending = read();
  if (!pending || pending.userId !== userId) return false;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const { data } = await api.post(
        "/learning/guest-progress",
        { ...pending.packet, accountId: userId },
        { timeout: 5000 },
      );
      if (
        !data?.acknowledged ||
        data.transferId !== pending.packet.transferId ||
        data.userId !== userId
      )
        return false;
      if (read()?.packet.transferId !== pending.packet.transferId) return true;
      clearTransferredGuestLessons(pending.lessons);
      clearTransferredGuestActivity(pending.activity);
      write(null);
      return true;
    } catch {
      return false;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}
/** Only a server-confirmed new account may automatically claim this browser's guest history. */
export async function carryGuestProgressOnAuthentication(
  userId: number,
  newAccount: boolean,
) {
  try {
    if (newAccount) queueGuestTransfer(userId);
    if (pendingGuestTransfer(userId)) await retryGuestTransfer(userId);
  } catch {
    /* Broken guest storage cannot prevent sign-in. */
  }
}
